import { useEffect, useRef } from 'react';

type ResizeReturnType = {
  width: number;
  height: number;
};

export const useResizeObserver = ({
  onBeforeResize,
  onAfterResize,
}: {
  onBeforeResize: ({ width, height }: ResizeReturnType) => void;
  onAfterResize: ({ width, height }: ResizeReturnType) => void;
}) => {
  const beforeResizeRef = useRef(onBeforeResize);
  const afterResizeRef = useRef(onAfterResize);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    beforeResizeRef.current = onBeforeResize;
    afterResizeRef.current = onAfterResize;
  }, [onBeforeResize, onAfterResize]);

  useEffect(() => {
    if (!ref.current) return;

    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const handleResize = ({ width, height }: ResizeReturnType) => {
      beforeResizeRef.current({ width, height });

      // Clear previous timeout
      if (timeoutId) clearTimeout(timeoutId);

      // Delay duration update
      timeoutId = setTimeout(() => {
        afterResizeRef.current({ width, height });
      }, 0);
    };

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (entry.contentRect) {
          handleResize({
            width: entry.contentRect.width,
            height: entry.contentRect.height,
          });
        }
      }
    });

    observer.observe(ref.current);

    // Set initial width
    handleResize({
      width: ref.current.clientWidth,
      height: ref.current.clientHeight,
    });

    return () => {
      observer.disconnect();
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []);

  return { ref };
};
